// Shared plumbing for the Cognito hosted-UI social sign-ins (Google, Facebook):
// whether the Facebook button is offered, how an error that comes back on the
// redirect is read and classified, and which language to show it in.
//
// Why this exists: signInWithRedirect() leaves the app for the provider and
// Cognito, and a failure comes back as a full page load of "/" (the registered
// callback URL) with `?error=...&error_description=...` and no UI of its own.
// Amplify raises a Hub event for it, but that event is dispatched while the
// bundle is still loading - before React mounts, and index.tsx can delay the
// mount by up to a second and a half for a returning visitor - so a Hub
// listener alone misses it and the person is left on the home page with no
// explanation. The URL is the reliable record, so it is read directly.

/** What the person needs to be told, not what went wrong technically. */
export type SocialSignInErrorCode = "denied" | "emailMissing" | "emailInUse" | "generic";

// Stable codes thrown by infra/lambda/presignup-guard/index.mjs. Cognito wraps
// them as "PreSignUp failed with error <code>." in error_description. Keep in sync.
export const FACEBOOK_NO_EMAIL = "NIHONGOTECH_FACEBOOK_NO_EMAIL";
export const FACEBOOK_EMAIL_IN_USE = "NIHONGOTECH_FACEBOOK_EMAIL_IN_USE";

export interface OAuthRedirectError {
    error: string;
    description: string;
}

/**
 * The Facebook button stays hidden until the owner has created the Meta app and
 * secret and deployed the stack with enableFacebook (docs/operations/facebook-
 * login-setup.md), then builds the site with REACT_APP_FACEBOOK_LOGIN_ENABLED=true.
 * Shipping the button first would send every visitor who clicks it to a Cognito
 * error page, because the app client would not list Facebook yet.
 */
export function isFacebookLoginEnabled(): boolean {
    return process.env.REACT_APP_FACEBOOK_LOGIN_ENABLED === "true";
}

/**
 * Reads a hosted-UI failure from the page the browser was sent back to. Only the
 * site root counts: that is the one callback URL registered on the app client,
 * so an `?error=` on any other page (the X sign-in's own finish page, a pasted
 * link) is not ours to handle.
 */
export function readOAuthRedirectError(pathname: string, search: string): OAuthRedirectError | null {
    if (pathname !== "/") return null;
    const params = new URLSearchParams(search);
    const error = params.get("error");
    if (!error) return null;
    return { error, description: params.get("error_description") ?? "" };
}

/**
 * Maps a provider/Cognito failure to something the person can act on. The
 * description is only ever matched, never displayed: it is attacker-influenced
 * text from a URL, and it can name internals.
 */
export function classifyOAuthError({ error, description }: OAuthRedirectError): SocialSignInErrorCode {
    // Facebook gave no email (phone-only account, or the email permission was
    // declined). Either our guard says so, or Cognito itself does
    // ("attributes required: [email]").
    if (description.includes(FACEBOOK_EMAIL_IN_USE)) return "emailInUse";
    if (description.includes(FACEBOOK_NO_EMAIL) || /attributes required.*email/i.test(description)) return "emailMissing";
    // Closing the provider dialog, pressing Cancel or refusing the permission.
    // Facebook reports error=access_denied / error_reason=user_denied / "Permissions error";
    // Amplify reports "User cancelled OAuth flow." when the person simply came back.
    if (error === "access_denied" || /permissions error|user_denied|access_denied|cancel/i.test(description)) return "denied";
    return "generic";
}

// ---------------------------------------------------------------------------
// Language to show the failure in. The callback URL is always the English root,
// so a person who started on /pt/login would otherwise be answered in English.
// ---------------------------------------------------------------------------
const RETURN_LANGUAGE_KEY = "nihongotech.oauth.returnLanguage";
const RETURN_LANGUAGE_TTL_MS = 30 * 60 * 1000;

export type ReturnLanguage = "en" | "pt";

export function rememberOAuthReturnLanguage(language: ReturnLanguage): void {
    try {
        sessionStorage.setItem(RETURN_LANGUAGE_KEY, JSON.stringify({ language, at: Date.now() }));
    } catch { /* storage unavailable: the error simply appears in English */ }
}

// The marker doubles as proof that THIS tab started a hosted-UI sign-in a moment ago.
// sessionStorage belongs to one tab and disappears when it closes, so an attempt that was
// abandoned in a tab that has since been closed leaves no marker behind.
function readReturnMarker(): { language: ReturnLanguage } | null {
    try {
        const stored = JSON.parse(sessionStorage.getItem(RETURN_LANGUAGE_KEY) || "null");
        if (
            (stored?.language === "en" || stored?.language === "pt") &&
            typeof stored.at === "number" &&
            Date.now() - stored.at < RETURN_LANGUAGE_TTL_MS
        ) {
            return { language: stored.language };
        }
    } catch { /* unreadable: no marker */ }
    return null;
}

/**
 * Whether this tab started a Google/Facebook sign-in in the last half hour, without
 * clearing the marker (the login-page redirect still needs it to pick the language).
 *
 * Amplify keeps an "OAuth in progress" flag in localStorage from the moment the button is
 * clicked. If the person never finishes (closes the provider's tab), the flag survives, and
 * on the next page load in ANY tab Amplify treats that page as the return trip, finds no
 * code, and reports "User cancelled OAuth flow." through its Hub event. Acting on that event
 * alone would bounce someone who just opened a blog post to the login page. It is a real
 * return only if this tab has the marker.
 */
export function hasPendingOAuthReturn(): boolean {
    return readReturnMarker() !== null;
}

/** Reads and clears the remembered language; anything missing, stale or odd is English. */
export function consumeOAuthReturnLanguage(): ReturnLanguage {
    const marker = readReturnMarker();
    try {
        sessionStorage.removeItem(RETURN_LANGUAGE_KEY);
    } catch { /* storage unavailable: nothing to clear */ }
    return marker?.language ?? "en";
}
